import { useState, useEffect, useCallback } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { Sidebar } from './sidebar';
import { Header } from './header';
import { useAuthStore } from '@/stores/auth-store';
import { trpc } from '@/lib/trpc';

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 1024);
  useEffect(() => {
    const mql = window.matchMedia('(max-width: 1023px)');
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, []);
  return isMobile;
}

export function DashboardLayout() {
  const isMobile = useIsMobile();
  const [sidebarOpen, setSidebarOpen] = useState(!isMobile);
  const { isAuthenticated, updatePermissions, logout } = useAuthStore();
  const location = useLocation();

  // Close sidebar on navigation when on mobile
  useEffect(() => {
    if (isMobile) {
      setSidebarOpen(false);
    }
  }, [location.pathname, isMobile]);

  // Close sidebar when switching to mobile, open when switching to desktop
  useEffect(() => {
    setSidebarOpen(!isMobile);
  }, [isMobile]);

  const closeSidebar = useCallback(() => setSidebarOpen(false), []);

  // Refresh grants for existing sessions after an administrator or a backfill changes them.
  const { data: permissionsData, error: permissionsError } = trpc.permission.getMyPermissions.useQuery(
    undefined, // No input needed - uses session context
    {
      enabled: isAuthenticated,
      retry: false,
      staleTime: 60_000,
      refetchOnWindowFocus: true,
      refetchInterval: 60_000,
    }
  );

  useEffect(() => {
    if (permissionsData) {
      updatePermissions(permissionsData as any);
    }
  }, [permissionsData, updatePermissions]);

  useEffect(() => {
    if (permissionsError?.data?.code === 'UNAUTHORIZED') {
      // Session is invalid or expired, log out
      logout();
    }
  }, [permissionsError, logout]);

  return (
    <div className="flex h-screen max-h-screen overflow-hidden bg-[hsl(var(--background))]">
      {/* Mobile overlay backdrop */}
      {isMobile && sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm transition-opacity"
          onClick={closeSidebar}
        />
      )}

      {/* Sidebar */}
      <Sidebar
        isOpen={sidebarOpen}
        onToggle={() => setSidebarOpen(!sidebarOpen)}
        isMobile={isMobile}
      />

      {/* Main Content */}
      <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
        <Header onMenuClick={() => setSidebarOpen(!sidebarOpen)} />

        <main className="flex-1 overflow-y-auto bg-gradient-to-br from-[hsl(var(--background))] to-[hsl(var(--muted))/30] p-4 md:p-6 lg:p-8">
          <div className="mx-auto max-w-7xl">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
